import { ArrowUpRight, Download, Clock, Phone, Mail } from "lucide-react";
import PageShell from "./PageShell";
import PageHero from "./PageHero";
import Admissions from "./Admissions";
import RelatedPages from "./RelatedPages";
import PullQuote from "./PullQuote";
import Reveal from "./Reveal";
import SectionOrnament from "./SectionOrnament";
import { admissionsInfo, admissionsExtra, contactInfo, ORIG } from "../mock";

export default function AdmissionsPage() {
  return (
    <PageShell>
      {({ openEnquire }) => (
        <>
          <PageHero
            eyebrow={admissionsInfo.session}
            title="Admissions"
            italic="Open."
            image={`${ORIG}/home-banner2.webp`}
            blurb="Pre-Nursery to Grade 9 - 12th — begin your child's journey at GD Goenka Signature."
          />

          {/* Intro */}
          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[900px] mx-auto px-5 md:px-10 text-center">
              <Reveal>
                <div className="eyebrow mb-4">A Holistic Approach</div>
                <p className="serif text-[16px] md:text-[19px] leading-[1.85] text-[var(--ink)]/80">
                  {admissionsExtra.intro}
                </p>
              </Reveal>
              <SectionOrnament />
            </div>
          </section>

          {/* 4-Step Process */}
          <section className="bg-[var(--cream)] py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[760px] mx-auto mb-10 md:mb-14">
                  <div className="eyebrow mb-4">Admissions Process</div>
                  <h2 className="h-section text-[var(--ink)]">
                    Four gentle steps to
                    <span className="serif-italic gold-text"> Signature.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {admissionsExtra.process.map((p, i) => (
                  <Reveal key={p.step} delay={i * 120} variant="zoom">
                    <article className="relative p-7 md:p-8 light-card h-full bg-white">
                      <div className="serif-italic gold-text text-[40px] md:text-[48px] leading-none opacity-90">
                        {p.step}
                      </div>
                      <h3 className="serif text-[22px] md:text-[26px] text-[var(--ink)] leading-tight mt-3">
                        {p.title}
                      </h3>
                      <span className="gold-divider-thin block my-4" />
                      <p className="text-[14.5px] leading-[1.75] text-[var(--ink)]/75">{p.body}</p>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <PullQuote
            tone="dark"
            text="Every great journey begins with a conversation. Drop us a note — we'll respond within one working day."
          />

          {/* Policy + Brochure */}
          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {/* Seat policy */}
              <Reveal variant="left">
                <div className="p-7 md:p-9 border border-[rgba(184,148,86,0.3)] light-card h-full">
                  <div className="eyebrow mb-4">Seat Allotment Policy</div>
                  <h3 className="serif text-[24px] md:text-[30px] text-[var(--ink)] leading-tight">
                    First-come,
                    <span className="block serif-italic gold-text">first-serve.</span>
                  </h3>
                  <span className="gold-divider-thin block my-5" />
                  <p className="text-[15px] leading-[1.85] text-[var(--ink)]/80">{admissionsExtra.policy}</p>

                  <div className="mt-6 flex items-center gap-3 text-[var(--ink)]/80">
                    <span className="w-9 h-9 border border-[var(--gold)] flex items-center justify-center">
                      <Clock size={14} className="text-[var(--gold)]" />
                    </span>
                    <div>
                      <div className="text-[10.5px] tracking-[0.24em] uppercase text-[var(--muted)]">Office Hours</div>
                      <div className="serif text-[15.5px] md:text-[17px]">{admissionsExtra.hours}</div>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Brochure + Contacts */}
              <Reveal variant="right" delay={100}>
                <div className="relative p-7 md:p-9 h-full" style={{ background: "var(--navy)" }}>
                  <div className="hidden md:block absolute -top-3 -left-3 w-14 h-14 border-l border-t border-[var(--gold)]" />
                  <div className="hidden md:block absolute -bottom-3 -right-3 w-14 h-14 border-r border-b border-[var(--gold)]" />
                  <div className="eyebrow mb-4">Downloads</div>
                  <h3 className="serif text-white text-[24px] md:text-[30px] leading-tight">
                    School
                    <span className="block serif-italic gold-text">brochure &amp; prospectus.</span>
                  </h3>
                  <span className="gold-divider-thin block my-5" />
                  <p className="text-white/75 text-[14.5px] leading-[1.8] mb-6">
                    A printable brochure with a tour of the campus, curriculum and residential life — perfect to share with family.
                  </p>
                  <a
                    href={admissionsExtra.brochureUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-gold inline-flex items-center gap-2 mb-7"
                    style={{ background: "var(--gold)", color: "var(--navy-deep)" }}
                  >
                    <Download size={16} /> Download Brochure
                  </a>

                  <div className="space-y-4 pt-6 border-t border-[rgba(184,148,86,0.2)]">
                    <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 group">
                      <span className="w-9 h-9 border border-[var(--gold)] flex items-center justify-center">
                        <Phone size={14} className="text-[var(--gold)]" />
                      </span>
                      <div>
                        <div className="text-[10.5px] tracking-[0.24em] uppercase text-[var(--gold-light)]">Talk to admissions</div>
                        <div className="serif text-white text-[15.5px] md:text-[17px] group-hover:text-[var(--gold)] transition-colors">{contactInfo.phone}</div>
                      </div>
                    </a>
                    <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 group">
                      <span className="w-9 h-9 border border-[var(--gold)] flex items-center justify-center">
                        <Mail size={14} className="text-[var(--gold)]" />
                      </span>
                      <div>
                        <div className="text-[10.5px] tracking-[0.24em] uppercase text-[var(--gold-light)]">Email us</div>
                        <div className="serif text-white text-[15.5px] md:text-[17px] group-hover:text-[var(--gold)] transition-colors">{contactInfo.email}</div>
                      </div>
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Quick CTA — existing component */}
          <Admissions onEnquire={openEnquire} />

          <RelatedPages current="/admissions.html" />
        </>
      )}
    </PageShell>
  );
}
